import type { CompanyGroupListItem } from "@/models/company-group.model";
import type { DataTableColumn } from "@spz/ui/components/table/types";
import { formatCellValue } from "@spz/ui/lib/utils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCreateOrUpdateCompanyGroupDialog } from "../dialogs/use-create-or-update-company-group-dialog";
import { useDeleteCompanyGroupDialog } from "../dialogs/use-delete-company-group-dialog";

export const useCompanyGroupsColumns = () => {
  const { t } = useTranslation();

  const editCompanyGroupDialog = useCreateOrUpdateCompanyGroupDialog();
  const deleteCompanyGroupPopover = useDeleteCompanyGroupDialog();

  return useMemo<DataTableColumn<CompanyGroupListItem>[]>(
    () => [
      {
        accessorKey: "name",
        header: t("group_name"),
        type: "text",
        cellProps: (row) => ({
          value: row.name,
          showTooltip: false,
        }),
      },
      {
        accessorKey: "description",
        header: t("description"),
        type: "text",
        cellProps: (row) => formatCellValue(row.description, "text"),
      },
      {
        accessorKey: "companyCount",
        header: t("affiliated_companies"),
        type: "numeric",
        cellProps: (row) => ({
          value: row.companyCount,
          showTooltip: false,
        }),
      },
      {
        accessorKey: "actions",
        header: "",
        type: "button",
        size: 150,
        minSize: 100,
        enableSorting: false,
        cellProps: (row: CompanyGroupListItem) => ({
          buttons: [
            {
              type: "icon",
              icon: "edit",
              label: t("edit_group"),
              variant: "ghost",
              onClick: () => {
                editCompanyGroupDialog.open({
                  groupId: row.id,
                });
              },
            },
            {
              type: "icon",
              icon: "trash-2",
              label: t("delete_group"),
              variant: "ghost",
              onClick: (e) => {
                deleteCompanyGroupPopover.open({
                  groupId: row.id,
                  anchorElement: e.currentTarget,
                  updatedAt: row.updatedAt.toISOString(),
                });
              },
            },
          ],
        }),
      },
    ],
    [t, deleteCompanyGroupPopover, editCompanyGroupDialog],
  );
};
